import { useEffect, useState } from 'react'
import { api, setSessionUser, refreshSessionUser, ApiError } from './api'
import type {Credential, CurrentUser} from './api'
import {getSessionUser, onSessionChange} from './api'

// 会话原语住在 api.ts（它是"怎么和后端说话"的层），这里转出去让上层
// 只需要 import 一个模块 —— 页面里同时引 api 与 auth 容易让人以为
// 两者是并列的两套状态管理。
export {getSessionUser, onSessionChange}
export type {CurrentUser}

/**
 * SaaS 认证与当前会话状态。
 *
 * 与迁移前的差异：**不再有 `session` 对象**。原来 supabase 返回
 * `{session, user}`，本项目实际只用 `session.user`，所以直接返回
 * `user` —— 少一层包装，上层改起来是 `session?.user` → `user`。
 */
export function useAuth() {
	const [user, setUser] = useState<CurrentUser | null>(null)
	const [ready, setReady] = useState(false)

	useEffect(() => {
		// onSessionChange 会立即回调一次当前态，ready 随之置位
		const off = onSessionChange(u => {
			setUser(u)
			setReady(true)
		})
		return () => {
			off()
		}
	}, [])

	return { user, ready }
}

/** 邮箱密码登录。成功后同步本地会话缓存，让 useAuth 立即更新。 */
export async function loginWithPassword(email: string, password: string): Promise<void> {
	const data = await api.post<{ user: CurrentUser }>('/auth/login', {
		email: email.trim(),
		password,
	})
	setSessionUser(data.user)
}

/**
 * 手机号 + 密码登录。
 *
 * 与邮箱密码登录走**同一个后端接口**（`/auth/login`）—— 后端按输入的形态
 * 自动分派（是手机号就按 identity 盲索引反查用户，是邮箱就按 email 查）。
 * 前端不必自己判断，输错了由后端统一回「账号或密码不正确」。
 *
 * ⚠️ 后端刻意**不区分**"手机号不存在"与"密码错误"，两种都回同一句 ——
 *    区分开就成了枚举接口（能挨个试出哪些号码注册过）。
 */
export async function loginWithPhone(phone: string, password: string): Promise<void> {
	const data = await api.post<{ user: CurrentUser }>('/auth/login', {
		phone: phone.trim(),
		password,
	})
	setSessionUser(data.user)
}

/**
 * 手机号验证码注册。
 *
 * 流程：手机号 → 收短信验证码 → 校验 → 设密码 → 建号 → **直接登录**
 * （后端返回时登录态已经建好，前端拿到 user 就能进首页）。
 *
 * `agreed_terms_version` / `agreed_terms_at` 是法律记录（同意的协议版本与
 * 时间），后端**没有它们就不建号** —— 与微信注册同一道闸。
 *
 * `already_registered` 为真表示这个号码**已经注册过**：后端没有建第二个号
 * （同一人两个手机号两份账号之后无法合并），而是直接登录了那个已有账号。
 * 前端据此给一句「这个号码已注册，已为你登录」而不是报"注册失败"。
 */
export async function registerWithPhone(params: {
	phone: string
	code: string
	password: string
	nickname?: string
	agreed_terms_version?: string
	agreed_terms_at?: string
}): Promise<{ user: CurrentUser, already_registered: boolean }> {
	const data = await api.post<{ user: CurrentUser, already_registered: boolean }>(
		'/auth/register/phone',
		{
			phone: params.phone.trim(),
			code: params.code.trim(),
			password: params.password,
			nickname: params.nickname ?? '',
			agreed_terms_version: params.agreed_terms_version ?? '',
			agreed_terms_at: params.agreed_terms_at ?? '',
		},
	)
	if (data?.user) setSessionUser(data.user)
	return data
}

/**
 * 邮箱注册。
 *
 * `agreement` 是法律记录（用户同意的协议版本与时间），原栈塞在
 * `auth.users.raw_user_meta_data` 里，现在存进后端的专列。
 * 传 undefined 时后端留空 —— 但前端注册页强制勾选协议，正常不会为空。
 */
export async function register(params: {
	email: string
	password: string
	nickname?: string
	agreed_terms_version?: string
	agreed_terms_at?: string
}): Promise<{ user: CurrentUser }> {
	return api.post<{ user: CurrentUser }>('/auth/register', {
		email: params.email.trim(),
		password: params.password,
		nickname: params.nickname ?? '',
		agreed_terms_version: params.agreed_terms_version ?? '',
		agreed_terms_at: params.agreed_terms_at ?? '',
	})
}

/**
 * 退出登录。
 *
 * ⭐ 登录态变化是靠「通知订阅者」传播的，不是靠窗口 focus 事件 ——
 * 所以页面停在编辑器里时退出，顶栏也会立刻变。这比原来
 * supabase 的方案更直接。
 */
export async function logout(): Promise<void> {
	await api.post('/auth/logout')
	setSessionUser(null)
}

/**
 * 设置新密码（登录态下）。
 *
 * 已设过密码的账号要传 oldPassword —— 拿到会话的人不该就能改掉密码，
 * 那会让他永久占住账号。微信注册的账号没设过密码，可省略。
 */
export async function changePassword(
	newPassword: string,
	oldPassword?: string,
): Promise<void> {
	await api.post('/auth/password', {
		new_password: newPassword,
		old_password: oldPassword ?? '',
	})
}

/** 发起密码重置。后端始终返回成功（防邮箱枚举）。 */
export async function requestPasswordReset(email: string): Promise<void> {
	await api.post('/auth/password/forgot', {email: email.trim()})
}

/** 凭 token 设置新密码 */
export async function confirmPasswordReset(
	uid: string,
	token: string,
	newPassword: string,
): Promise<void> {
	await api.post('/auth/password/reset', {
		uid,
		token,
		new_password: newPassword,
	})
}

/**
 * 微信扫码登录。
 *
 * ## 现在用 iframe 内嵌（二维码直接显示在页签里）
 *
 * 早前这里写的是「qrconnect 不能嵌 iframe，只能开新窗口」—— **那是错的**，
 * 是没实测就下的结论。2026-10-19 拉了微信返回的页面源码来看，它自己的脚本
 * 是这么写的：
 *
 * ```js
 * var v = window.top != window;              // v = 是否在 iframe 内
 * if (v) { document.domain = "qq.com";
 *          if (window.top.location.host) window.location = redirect_uri }
 * else  window.top.location = redirect_uri;  // 不在 iframe 才动顶层
 * ```
 *
 * 识别到自己在 iframe 里就只导航自己 —— 明确支持内嵌（微信官方 `WxLogin`
 * 组件本身就是 iframe 实现的）。响应头也没有 `X-Frame-Options`。
 *
 * ## 内嵌之后要解决的两件事
 *
 * 1. **回调不能跳前端页面**。iframe 里 302 到首页（或登录页）会把整个
 *    应用在 iframe 里再渲染一遍。所以 embed 回调（路径 `/callback/embed`）
 *    成功与失败都只回一个中转页，由它 postMessage 通知父窗口。
 * 2. **父窗口怎么知道登录成功**。iframe 内发生的一切（同源 cookie 写入、
 *    后端 302）父窗口都看不见，跨窗口也没有可用的 JS 触发点 ——
 *    后端返回的是重定向，不是我们能控制的 postMessage。
 *    所以靠 `message` 事件（见 AuthPage）＋ `refreshSessionAfterWechat` 兜底。
 *
 * 邮箱密码登录不需要这两步（响应里就带着 user），这是两条路径唯一的实质差别。
 */

/**
 * 取微信扫码登录地址。
 *
 * 后端未配置微信（AppID 还没下来）时返回 503，调用方据此
 * **只显示邮箱登录入口** —— 而不是弹一个"微信登录失败"。
 *
 * ⚠️ 注意 `code` 的形状：后端 `fail()` 抛出的 ApiError 里 `code` 取自
 *    响应体的 `code` 字段。仅 503 不足以判断是"没配置"，别的依赖缺失
 *    也可能给 503，所以两边都判一次。
 *
 * `embed` 为 true 时向后端要一条**给 iframe 用**的地址：它的
 * redirect_uri 是专用路径 `/callback/embed`，回调据此回 postMessage
 * 中转页（成败都是），而不是 302 到前端 —— 302 会把整个应用渲染进
 * 300×400 的扫码框。这条地址必须由后端拼，前端自己改 URL 会对不上。
 *
 * `agreeVersion` / `shareVersion` 传两份文档的版本号（用户勾选后才传）：
 * 《用户服务协议》与《曲谱公开分享免责声明》。它们跟着这条请求进后端会话，
 * 回调**建号那一刻**写进 `auth_user`，这就是「同意的是哪一版」的凭证；
 * 不传时后端会拒绝新建账号（老用户登录不受影响）。
 */
export async function fetchWechatQrUrl(
	embed = false,
	agreeVersion?: string,
	shareVersion?: string,
): Promise<string | null> {
	try {
		// `agree` / `share` 是**协议同意记录**，不是渲染参数：后端把它们存进该
		// 会话，微信回调真正建号时再写进 auth_user.agreement_version。所以必须
		// 跟取二维码这同一个请求走 —— 前端拿不到一次「建号成功」的回调，
		// 事后补写会漏掉「扫完就关页面」的那部分用户。
		const qs = new URLSearchParams()
		if (embed) qs.set('ui', 'embed')
		if (agreeVersion) qs.set('agree', agreeVersion)
		if (shareVersion) qs.set('share', shareVersion)
		const path = `/auth/wechat/qr${qs.toString() ? `?${qs}` : ''}`
		const data = await api.get<{ url: string }>(path)
		return data.url
	} catch (err) {
		if (err instanceof ApiError && (err.code === 'wechat_not_configured' || err.status === 503)) {
			return null
		}
		throw err
	}
}

/**
 * 扫码窗口关闭后，把登录态同步回当前窗口。
 *
 * 返回后端确认的当前登录者；仍是 null 表示用户还没扫（放弃了）。
 *
 * 为什么不能直接用 `getSessionUser()`：那个函数有 `loaded` 缓存，
 * 首次加载之后就永远不再往返，而这里恰恰需要强制问一次。细节见
 * api.ts 的 `refreshSessionUser`。
 */
export async function refreshSessionAfterWechat(): Promise<CurrentUser | null> {
	return refreshSessionUser()
}

/**
 * 展示用用户名。优先级：用户设的昵称 → 邮箱 @ 前部分 → '用户'。
 *
 * 微信登录没有邮箱，所以昵称这条在实际使用中占绝大多数。
 */
export function displayName(user: CurrentUser | null): string {
	if (!user) return ''
	if (user.nickname) return user.nickname
	if (user.email) {
		const at = user.email.indexOf('@')
		return at > 0 ? user.email.slice(0, at) : user.email
	}
	return '用户'
}

/** 邮箱 @ 前的部分（默认用户名）。没有邮箱时回空串。 */
export function emailLocalPart(user: CurrentUser | null): string {
	if (!user?.email) return ''
	const at = user.email.indexOf('@')
	return at > 0 ? user.email.slice(0, at) : user.email
}

// --------------------------------------------------------------------
// 验证码（全站无密码方案）
// --------------------------------------------------------------------

export type CodeChannel = 'email' | 'sms'

/**
 * 发码的用途。
 *
 * `login`（默认）：这个地址必须是已注册的，否则后端直接回「未注册」不发码。
 * `register`：**允许是未注册的** —— 注册的前提就是"还没注册"，判了等于
 *              把注册路径堵死。只对手机号开放（邮箱自助注册仍关闭）。
 */
export type CodePurpose = 'login' | 'register'

/** 发送验证码。返回有效期（秒）。 */
export async function requestCode(
	channel: CodeChannel,
	target: string,
	purpose: CodePurpose = 'login',
): Promise<{ttl: number}> {
	return api.post<{ttl: number}>('/auth/code/request', {
		channel,
		target: target.trim(),
		purpose,
	})
}

/**
 * 核销验证码。
 *
 * 后端按**当前是否登录**分成两条完全不同的语义，前端不要自己判断：
 *   · 已登录 → 把该邮箱/手机号**绑到当前账号**（`bound: true`）
 *   · 未登录 → 用它**登录**（`bound: false`）
 *
 * 所以同一个函数在登录页与设置页都能用。返回的 user 是权威值，
 * 拿到就直接 setSessionUser，别自己拼。
 */
export async function verifyCode(
	channel: CodeChannel,
	target: string,
	code: string,
): Promise<{user: CurrentUser, bound: boolean}> {
	const data = await api.post<{user: CurrentUser, bound: boolean}>('/auth/code/verify', {
		channel,
		target: target.trim(),
		code: code.trim(),
	})
	if (data?.user) setSessionUser(data.user)
	return data
}

export type IdentitySummary = {
	credentials: Credential[]
	need_credential: boolean
	/** 哪几种验证方式真的能发出去。手机号在短信开通前是 false */
	available: {email: boolean, sms: boolean}
}

/**
 * 登录页用的**公开**渠道探测（不需要登录）。
 *
 * 为什么登录页不能直接用 `fetchIdentities()`：那个接口要登录态（`@require_login`），
 * 登录页恰恰是还没登录的时候。它返回的 `credentials` 里有掩码与绑定状态，
 * 登录页也不该知道 —— 这个接口只回答"哪种方式现在能用"。
 */
export async function fetchAuthChannels(): Promise<{
	available: {email: boolean, sms: boolean}
	wechat: boolean
	phone_signup: boolean
}> {
	return api.get('/auth/channels')
}

/** 当前账号的登录方式。设置页与「完善账号」弹窗用它决定显示什么。 */
export async function fetchIdentities(): Promise<IdentitySummary> {
	return api.get<IdentitySummary>('/auth/identities')
}

/** 解绑一种登录方式。后端会拒绝「解绑最后一个」。 */
export async function unbindIdentity(kind: Credential['kind']): Promise<{credentials: Credential[]}> {
	return api.post<{credentials: Credential[]}>(`/auth/identities/${kind}/unbind`)
}